import type { ExportPreferences } from '@gfe/core/store/user-preferences/user-preferences.model';
import { isNil } from '@gfe/core/utils/shared-utils';
import { ISO8601_DURATION_REGEX } from '@gfe/core/utils/time-utils';
import { processDurationValue } from '@gfe/shared/components/table/table-cell/utils';
import { TableColumn } from '@gfe/shared/components/table/table.model';
import { getCellValue } from '@gfe/shared/utils/table-cell.utils';

/**
 * The columns whose values are ISO 8601 durations, in the given column order. The
 * CSV transformer schemas declare repeatable columns under `patternProperties` as
 * plain strings — a job's `pickup_duration`, which may recur as
 * `pickup_duration_2` — so the schema alone cannot list every duration column;
 * a column whose non-empty values all parse as a duration is one.
 */
export function detectDurationColumns(
  columns: string[],
  rows: Array<Record<string, unknown>>
): string[] {
  return columns.filter((column) => {
    let seen = false;
    for (const row of rows) {
      const value = row[column];
      if (isNil(value) || value === '') continue;
      if (typeof value !== 'string' || !ISO8601_DURATION_REGEX.test(value)) return false;
      seen = true;
    }
    return seen;
  });
}

/**
 * The CSV column key that will hold a duration's clock rendering: `_hms` appended
 * to the field (`duration` -> `duration_hms`). `taken` guards against clobbering
 * an existing column, falling back to `_clock` and then `_hms_duration`.
 */
export function durationHmsColumnKey(field: string, taken: Set<string>): string {
  const suffixed = `${field}_hms`;
  if (!taken.has(suffixed)) return suffixed;
  const clock = `${field}_clock`;
  if (!taken.has(clock)) return clock;
  return `${field}_hms_duration`;
}

/**
 * Resolves a stable clock-column key for each duration field, threading a shared
 * `taken` set so two durations never collide on the same generated key.
 */
export function durationHmsColumnKeys(
  durationFields: string[],
  existingColumns: Iterable<string>
): Map<string, string> {
  const taken = new Set<string>(existingColumns);
  const keys = new Map<string, string>();
  for (const field of durationFields) {
    const key = durationHmsColumnKey(field, taken);
    keys.set(field, key);
    taken.add(key);
  }
  return keys;
}

/**
 * Adds a clock-formatted column to each row for every duration field. Input rows
 * are left untouched.
 */
export function augmentRowsWithDurationHms<T extends Record<string, unknown>>(
  rows: T[],
  durationFields: string[],
  hmsKeyByField: Map<string, string>
): T[] {
  if (durationFields.length === 0) return rows;
  return rows.map((row) => {
    const augmented: Record<string, unknown> = { ...row };
    for (const field of durationFields) {
      const hmsKey = hmsKeyByField.get(field);
      if (!hmsKey) continue;
      augmented[hmsKey] = processDurationValue(row[field], 'hms');
    }
    return augmented as T;
  });
}

/**
 * Applies the export preferences to an ordered column list: a duration's own
 * column is dropped when `includeDuration` is off, and its clock column is
 * inserted immediately after when `includeDurationHms` is on. Returns the input
 * array unchanged (same reference) for the default case (duration on, clock off).
 */
export function buildDurationExportColumns(
  baseColumns: string[],
  durationFields: string[],
  prefs: ExportPreferences,
  hmsKeyByField: Map<string, string>
): string[] {
  const includeDuration = prefs.includeDuration !== false;
  const includeHms = prefs.includeDurationHms === true;
  if (includeDuration && !includeHms) return baseColumns;

  const fields = new Set(durationFields);
  const result: string[] = [];
  for (const col of baseColumns) {
    if (!fields.has(col)) {
      result.push(col);
      continue;
    }
    if (includeDuration) result.push(col);
    if (includeHms) {
      const hmsKey = hmsKeyByField.get(col);
      if (hmsKey) result.push(hmsKey);
    }
  }
  return result;
}

/**
 * Whether a displayed column holds a duration, and so is subject to the export
 * duration toggles. Covers both schema-typed duration cells and columns flagged
 * with `isDuration` because their renderHook already formats the value.
 */
export function isExportDurationColumn<T>(column: TableColumn<T>): boolean {
  return (
    column.type === 'duration' || column.type === 'durations' || column.isDuration === true
  );
}

/**
 * The clock rendering of a duration cell. Read through `getCellValue` so computed
 * columns run their `renderHook` and the conversion starts from exactly what the
 * table displays, whether that is an ISO 8601 duration, a number of seconds or an
 * already unit-formatted string.
 */
export function durationHmsCellValue<T>(row: T, column: TableColumn<T>): string {
  return processDurationValue(getCellValue(row, column), 'hms');
}
